import { Box, Tooltip, Typography, styled } from '@mui/material';
import { formatNumberToStringMonth, getDaysFromBinaryArray } from '../../../utils/dateUtils';
import i18n from '../../../i18n';
import ActiveSwitch from './ActiveSwitch';
import RowOperation from './RowOperation';
import { IActivity } from '../../../redux/activity/activitySlice';

interface IRenderCellParams {
  row: IActivity;
  value: string | boolean | IActivity;
}

interface IColumn {
  field: string;
  headerName: string;
  hide?: boolean;
  width?: number;
  disableColumnMenu?: boolean;
  sortable?: boolean;
  flex?: number;
  maxWidth?: number;
  resizable?: boolean;
  renderCell?: (_params: IRenderCellParams) => React.ReactNode;
  renderHeader?: (_params: IRenderCellParams) => React.ReactNode;
}

interface IStyledTypographyProps {
  backgroundColor?: string;
  textColor?: string;
}

const StyledTypography = styled(Typography)<IStyledTypographyProps>(
  ({ backgroundColor, textColor }) => ({
    borderRadius: 5,
    background: backgroundColor,
    padding: '4px 0',
    width: 255,
    textAlign: 'center',
    '& span': {
      background: textColor,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
  })
);

export const columns: IColumn[] | undefined = [
  { field: 'id', headerName: 'ID', hide: true },
  {
    field: 'name',
    headerName: i18n.t('activity.activityTable.title'),
    disableColumnMenu: true,
    sortable: false,
    flex: 2.2,
    resizable: false,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary.main">
          {i18n.t('activity.activityTable.title')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <Box display="flex" gap="12px" flexDirection="row">
        <Typography variant="bodyRegular" color="primary.main">
          {typeof params.value === 'string'
            ? params.value.charAt(0).toUpperCase() + params.value.slice(1)
            : ''}
        </Typography>
        {params.row.isUserCreated && params.row.isSharedWithCoach && (
          <Tooltip
            title={i18n.t('activity.activityTable.clientCreatedActivity')}
            placement="bottom-start"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'primary.main',
                  borderRadius: '5px',
                  padding: '4px 8px',
                },
              },
              arrow: {
                sx: {
                  '&:before': {
                    bgcolor: 'primary.main',
                  },
                },
              },
            }}
          >
            <img src="/assets/icons/user-activity-table.svg" alt="user-activity-table" />
          </Tooltip>
        )}
      </Box>
    ),
  },
  {
    field: 'startAt',
    headerName: i18n.t('activity.activityTable.assignedDate'),
    disableColumnMenu: true,
    sortable: false,
    flex: 1,
    resizable: false,
    renderHeader: () => (
      <Box display="flex" gap="10px">
        <Typography variant="meta" color="primary.main">
          {i18n.t('activity.activityTable.assignedDate')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <Box display="flex" color="primary.main">
        {typeof params.value === 'string' ? formatNumberToStringMonth(params.value) : ''}
      </Box>
    ),
  },
  {
    field: 'schedule',
    headerName: i18n.t('activity.activityTable.activityType'),
    disableColumnMenu: true,
    sortable: false,
    resizable: false,
    hide: true,
    flex: 2,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary.main">
          {i18n.t('activity.activityTable.activityType')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <Box display="flex">
        {(() => {
          switch (params.value) {
            case 'SITUATIONAL':
              return (
                <StyledTypography
                  variant="tinyTexts"
                  fontWeight={700}
                  backgroundColor="linear-gradient(175deg, rgba(255, 203, 203, 0.15) -9.81%, 
                  rgba(255, 127, 127, 0.15) 96.32%)"
                  textColor="var(--Alert-Sunset-Orange, linear-gradient(175deg, #FF9A9A -9.81%, #FF5858 96.32%))"
                >
                  <span>{i18n.t('activity.activityTable.custom')}</span>
                </StyledTypography>
              );
            case 'RECCURENT':
              return (
                <StyledTypography
                  variant="tinyTexts"
                  fontWeight={700}
                  backgroundColor="linear-gradient(175deg, rgba(146, 207, 205, 0.15) -9.81%, 
                  rgba(51, 110, 107, 0.15) 96.32%)"
                  textColor="var(--Alert-Myrtle-Green, linear-gradient(175deg, #92CFCD -9.81%, #336E6B 96.32%))"
                >
                  <span>{i18n.t('activity.activityTable.repetitive')}</span>
                </StyledTypography>
              );
            case 'ONETIME':
              return (
                <StyledTypography
                  variant="tinyTexts"
                  fontWeight={700}
                  backgroundColor="linear-gradient(175deg, rgba(87, 177, 90, 0.15) -9.81%, 
                  rgba(47, 125, 50, 0.15) 96.32%)"
                  textColor="var(--Alert-Japanese-Laurel, linear-gradient(175deg, #57B15A -9.81%, #2F7D32 96.32%))"
                >
                  <span>{i18n.t('activity.activityTable.oneTime')}</span>
                </StyledTypography>
              );
            default:
              return <Typography>{String(params.value)}</Typography>;
          }
        })()}
      </Box>
    ),
  },
  {
    field: 'frequency',
    headerName: i18n.t('activity.activityTable.frequency'),
    disableColumnMenu: true,
    sortable: false,
    resizable: false,
    flex: 2,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary.main">
          {i18n.t('activity.activityTable.frequency')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <Box display="flex">
        <Typography variant="bodyRegular" color="primary.main">
          {(() => {
            switch (params.row.schedule) {
              case 'SITUATIONAL':
                return params.row.customMessage;
              case 'RECCURENT':
                return getDaysFromBinaryArray(params.row.repeatOn || []);
              case 'ONETIME':
                return i18n.t('clientProgress.oneTime');
              default:
                return '';
            }
          })()}
        </Typography>
      </Box>
    ),
  },
];

export const activeClientColumns: IColumn[] | undefined = [
  ...columns,
  {
    field: 'status',
    headerName: i18n.t('activity.activityTable.status'),
    disableColumnMenu: true,
    sortable: false,
    resizable: false,
    flex: 1,
    renderHeader: () => (
      <Box display="flex">
        <Typography variant="meta" color="primary.main">
          {i18n.t('activity.activityTable.status')}
        </Typography>
      </Box>
    ),
    renderCell: (params: IRenderCellParams) => (
      <ActiveSwitch isActive={params.row.isActive} rowId={params.row.id} />
    ),
  },
  {
    field: 'option',
    headerName: '',
    disableColumnMenu: true,
    sortable: false,
    flex: 1,
    resizable: false,
    renderCell: (params: IRenderCellParams) => <RowOperation row={params.row} />,
  },
];
